import { useState, useEffect, ChangeEvent, useRef } from 'react';
import { Combobox } from '@headlessui/react';
import CheckIcon from '../ui/Icons/CheckIcon';
import { ChevronUpDownIcon } from '../ui/Icons';
import { classNames } from '@/lib/helpers/functions';
import { SelectOptionType } from '@/lib/types/BaseType';
import useDebounce from '@/hooks/useDebounce'; // Make sure to create this custom hook or use lodash debounce
import axios from 'axios';
import { useTranslations } from 'next-intl';
import useLanguageChange from '@/hooks/useLanguageChange';
import clsx from 'clsx';
import { withCacheTTL } from '@/clients/common/with-cache-ttl';

interface SelectWithSearchProps {
  apiEndpoint: string;
  paramKey: string;
  onSelect: (value: SelectOptionType) => void;
  onChange?: (value: ChangeEvent<HTMLInputElement>) => void;
  onButtonClick?: () => void;
  selectedOption?: SelectOptionType;
  placeholder?: string;
  extraParams?: Record<string, any>;
  initalParams?: Record<string, any>;
  inputClass?: string;
  roundClass?: string;
  setInitialRemoteData?: (paylaod: any) => void;
  setRemoteData?: (paylaod: any) => void;
}

export default function SelectWithRemoteSearch(props: SelectWithSearchProps) {
  const t = useTranslations();
  const {
    onSelect,
    onChange,
    onButtonClick,
    selectedOption,
    apiEndpoint,
    paramKey,
    placeholder,
    extraParams,
    initalParams,
    inputClass = '',
    roundClass = 'rounded-md',
    setInitialRemoteData,
  } = props;
  const [query, setQuery] = useState('');
  const [selectOptions, setSelectOptions] = useState<SelectOptionType[]>([]);
  const [loading, setLoading] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const { localeCode } = useLanguageChange();

  // Debounce the search query to prevent excessive API calls
  const debouncedQuery = useDebounce(query);

  const fetchFunction = async (params: any, headers: any) => {
    const response = await axios.get(apiEndpoint, {
      params,
      headers,
    });
    return response.data;
  };

  // Cached version of the fetch function
  const cachedFetch = withCacheTTL(fetchFunction, apiEndpoint, 60000);

  // Fetch initial data and also search results
  useEffect(() => {
    const fetchOptions = async () => {
      let params = {};

      if (debouncedQuery?.length > 0) {
        setLoading(true);
        params = {
          [paramKey]: debouncedQuery,
          ...extraParams,
        };
      } else {
        params = {
          ...extraParams,
          ...initalParams,
        };
      }
      try {
        // const response = await axios.get(apiEndpoint, {
        //   params: params,
        //   headers: {
        //     ['Accept-Language']: localeCode,
        //   },
        // });
        const data = await cachedFetch(params, {
          'Accept-Language': localeCode,
        });
        setSelectOptions(data?.data);
        setInitialRemoteData?.(data);
      } catch (error) {
        console.error('Error fetching options:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, [debouncedQuery, apiEndpoint, localeCode]);

  useEffect(() => {
    const inputElement: any = inputRef.current;
    let observer: MutationObserver | null = null;

    const handleStateChange = () => {
      if (inputElement) {
        const isOpen =
          inputElement.getAttribute('data-headlessui-state') === 'open';
        if (isOpen) {
          inputElement.value = ''; // Clear the input when open
        } else {
          inputElement.value = selectedOption?.name ?? ''; // Restore the previous value when closed
        }
      }
    };

    const handleFocus = () => {
      if (inputElement) inputElement.value = ''; // Clear the input on focus
    };

    const handleBlur = () => {
      if (inputElement) inputElement.value = selectedOption?.name; // Restore the previous value on blur
    };

    if (query?.length <= 0) {
      // Add event listeners for focus and blur
      inputElement.addEventListener('focus', handleFocus);
      inputElement.addEventListener('blur', handleBlur);

      // Initialize the MutationObserver
      observer = new MutationObserver(handleStateChange);
      observer.observe(inputElement, {
        attributes: true,
        attributeFilter: ['data-headlessui-state'],
      });
    }

    // Cleanup function
    return () => {
      if (inputElement) {
        inputElement.removeEventListener('focus', handleFocus);
        inputElement.removeEventListener('blur', handleBlur);
      }
      if (observer) {
        observer.disconnect();
      }
    };
  }, [selectedOption, query]);

  return (
    <Combobox
      as="div"
      value={selectedOption}
      onChange={(value) => {
        if (inputRef.current) {
          inputRef.current?.focus();
        }
        if (value) {
          onSelect(value);
        }
      }}
      className="z-[9]"
    >
      <div className="relative">
        <Combobox.Input
          ref={inputRef}
          className={clsx(
            'border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6',
            inputClass,
            roundClass
          )}
          onChange={(event) => {
            setQuery(event.target.value);
            onChange?.(event);
          }}
          displayValue={(option: SelectOptionType) => option?.name ?? ''}
          placeholder={placeholder ?? t('search')}
          aria-label={placeholder ?? t('search')}
        />
        <Combobox.Button
          onClick={() => {
            onButtonClick?.();
          }}
          className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none"
        >
          <ChevronUpDownIcon
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </Combobox.Button>

        {loading && (
          <div className="absolute z-[9999] mt-1 w-full bg-white py-1 text-center text-gray-500 shadow-lg ring-1 ring-black ring-opacity-5">
            {t('common.loading')}
          </div>
        )}

        {selectOptions.length > 0 && (
          <Combobox.Options className="absolute z-[9999] mt-1 max-h-60 w-full overflow-y-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {selectOptions.map((option: SelectOptionType) => (
              <Combobox.Option
                key={option.id}
                value={option}
                className={({ active }) =>
                  classNames(
                    'relative z-[9999] cursor-default select-none py-2 pl-3 pr-9',
                    active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                  )
                }
              >
                {({ active, selected }) => (
                  <div>
                    <span
                      className={classNames(
                        'block truncate',
                        selected ? 'font-semibold' : ''
                      )}
                    >
                      {option.name}
                    </span>

                    {selected && (
                      <span
                        className={classNames(
                          'absolute inset-y-0 right-0 flex items-center pr-4',
                          active ? 'text-white' : 'text-indigo-600'
                        )}
                      >
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    )}
                  </div>
                )}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  );
}
